import React, { useMemo } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useExternalLinks } from '@/app/hooks/useExternalLinks';
import { PayoutsForm } from '@/modules/onboarding/model/payoutsStep';
import { RUPartnerType } from '@/modules/partner/model/partnerType';
import { SelectField } from '@src-common/view/adapters/reactHookForm/SelectField';
import { TextField } from '@src-common/view/adapters/reactHookForm/TextField';

import s from '../styles.module.css';

type Props = {
  control: Control<PayoutsForm>;
};

const RuPaymentType: React.FC<Props> = ({ control }) => {
  const intl = useIntl();
  const links = useExternalLinks();

  const partnerTypes = useMemo(
    () => [
      {
        title: intl.formatMessage({
          defaultMessage: 'Pay individual entrepreneur',
          id: 'kgzbXX',
          description: 'RU Payout type option title - Pay individual entrepreneur (На счет ИП)',
        }),
        value: RUPartnerType.ip,
      },
      {
        title: intl.formatMessage({
          defaultMessage: 'Pay my company',
          id: 'qNnWaK',
          description: 'RU Payout type option title - Pay my company (На счет ООО)',
        }),
        value: RUPartnerType.ooo,
      },
      {
        title: intl.formatMessage({
          defaultMessage: 'Pay myself',
          id: '6Sy04Z',
          description: 'Payout type option title - Pay myself',
        }),
        value: RUPartnerType.selfEmployed,
      },
    ],
    [intl],
  );

  const selectedPartnerType = useWatch({ control, name: 'partnerType' });

  const isSelfEmployed = selectedPartnerType === RUPartnerType.selfEmployed;

  return (
    <>
      <SelectField
        control={control}
        name="partnerType"
        title={intl.formatMessage({
          defaultMessage: 'PAYOUT TYPE',
          id: 'MafFrv',
          description: 'PAYOUT TYPE field title',
        })}
        items={partnerTypes}
        description={
          isSelfEmployed
            ? intl.formatMessage({
                defaultMessage: 'If you don’t yet have an IE or OOO registration certificate.',
                id: 'Gai39E',
                description: 'Field description',
              })
            : undefined
        }
      />

      {selectedPartnerType === RUPartnerType.ip && (
        <div className={s.fieldIp}>
          <TextField
            shouldUnregister={true}
            name="companyName"
            control={control}
            isDisabled={true}
          />
        </div>
      )}

      {selectedPartnerType === RUPartnerType.ooo && (
        <>
          <TextField
            shouldUnregister={true}
            name="inn"
            title={intl.formatMessage({
              defaultMessage: 'COMPANY INN',
              id: 'dxJSd0',
              description: 'RU Company INN field title (ИНН компании)',
            })}
            placeholder={intl.formatMessage({
              defaultMessage: '770000000',
              id: 'kAtPKo',
              description: 'Company INN field placeholder',
            })}
            description={intl.formatMessage({
              defaultMessage: 'INN consits of 10 digits.',
              id: '/VIhvk',
              description: 'RU Company INN field hint',
            })}
            control={control}
          />
          <TextField
            shouldUnregister={true}
            name="companyName"
            title={intl.formatMessage({
              defaultMessage: 'COMPANY NAME',
              id: 'w8qpaR',
              description: 'COMPANY NAME field title',
            })}
            control={control}
          />
          <TextField
            shouldUnregister={true}
            name="kpp"
            title={intl.formatMessage({
              defaultMessage: 'KPP',
              id: 'lNdSmB',
              description: 'RU COMPANY KPP field title',
            })}
            placeholder={intl.formatMessage({
              defaultMessage: '770000000',
              id: 'klcdsT',
              description: 'RU Company KPP field placeholder',
            })}
            control={control}
          />
          <TextField
            shouldUnregister={true}
            name="companyAddress"
            control={control}
            title={intl.formatMessage({
              defaultMessage: 'COMPANY ADDRESS',
              id: 'jfwv8e',
              description: 'COMPANY ADDRESS field title',
            })}
          />
        </>
      )}

      {isSelfEmployed && (
        <div className={s.description}>
          <FormattedMessage
            defaultMessage="To take bookings with Wheely you’ll need to register as an IE (individual entrepreneur) or an OOO (limited liability company), or join a partner company."
            id="+SwBTX"
            description="RU Self employed limitation description"
          />
          <br />
          <br />
          <FormattedMessage
            defaultMessage="For more information please check the <a>‘Gosuslugi’ website.</a>"
            id="gcdC66"
            description="RU Self employed limitation description"
            values={{
              a: (text: React.ReactNode) => (
                <a href={links.registerIPorOOO} target="_blank" rel="noreferrer">
                  {text}
                </a>
              ),
            }}
          />
        </div>
      )}
    </>
  );
};

export { RuPaymentType };
